import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  TextField, 
  Button, 
  MenuItem, 
  Box,
  Alert,
  IconButton
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { GapFinding } from '../../services/gapAnalysisService';

interface EditFindingFormProps {
  finding: GapFinding | null;
  controlName: string;
  open: boolean;
  onClose: () => void;
  onSave: (findingId: string, updates: Partial<GapFinding>) => Promise<void>;
}

export default function EditFindingForm({ 
  finding,
  controlName,
  open, 
  onClose, 
  onSave 
}: EditFindingFormProps) {
  const [formData, setFormData] = useState<{
    evidence: string;
    remediationPlan: string;
    status: 'Open' | 'InProgress' | 'Resolved' | 'Accepted';
    priority: 'Critical' | 'High' | 'Medium' | 'Low';
    currentState: 'Compliant' | 'NonCompliant' | 'Partial' | 'NotApplicable';
  }>({
    evidence: '',
    remediationPlan: '',
    status: 'Open',
    priority: 'Medium',
    currentState: 'NonCompliant'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (finding) {
      setFormData({
        evidence: finding.evidence || '',
        remediationPlan: finding.remediationPlan || '',
        status: finding.status,
        priority: finding.priority,
        currentState: finding.currentState
      });
    }
  }, [finding]);

  const handleSubmit = async () => {
    if (!finding) {
      console.error('No finding to update');
      return;
    }
    
    console.log('handleSubmit called with finding:', finding);
    console.log('Form data:', formData);
    
    if (!formData.evidence.trim()) {
      setError('Evidence is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('Calling onSave with:', finding._id, formData);
      await onSave(finding._id, formData);
      console.log('onSave completed successfully');
      onClose();
    } catch (err: any) {
      console.error('Error in handleSubmit:', err);
      setError(err.response?.data?.error || err.message || 'Failed to update finding');
    } finally {
      setLoading(false);
    }
  };

  if (!finding) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Edit Finding - {controlName}
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          label="Evidence"
          fullWidth
          required
          multiline
          rows={4}
          sx={{ mb: 3 }}
          value={formData.evidence}
          onChange={(e) => setFormData({...formData, evidence: e.target.value})}
          placeholder="Describe the gap or non-compliance..."
        />

        <TextField
          label="Remediation Plan"
          fullWidth
          multiline
          rows={3}
          sx={{ mb: 3 }}
          value={formData.remediationPlan}
          onChange={(e) => setFormData({...formData, remediationPlan: e.target.value})}
          placeholder="Describe how this will be remediated..."
        />

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <TextField
            select
            label="Current State"
            fullWidth
            value={formData.currentState}
            onChange={(e) => setFormData({...formData, currentState: e.target.value as 'Compliant' | 'NonCompliant' | 'Partial' | 'NotApplicable'})}
          >
            <MenuItem value="Compliant">Compliant</MenuItem>
            <MenuItem value="Partial">Partial</MenuItem>
            <MenuItem value="NonCompliant">Non-Compliant</MenuItem>
            <MenuItem value="NotApplicable">Not Applicable</MenuItem>
          </TextField>

          <TextField
            select
            label="Status"
            fullWidth
            value={formData.status}
            onChange={(e) => setFormData({...formData, status: e.target.value as 'Open' | 'InProgress' | 'Resolved' | 'Accepted'})}
          >
            <MenuItem value="Open">Open</MenuItem>
            <MenuItem value="InProgress">In Progress</MenuItem>
            <MenuItem value="Resolved">Resolved</MenuItem>
            <MenuItem value="Accepted">Accepted</MenuItem>
          </TextField>
        </Box>

        <TextField
          select
          label="Priority"
          fullWidth
          sx={{ mb: 3 }}
          value={formData.priority}
          onChange={(e) => setFormData({...formData, priority: e.target.value as 'Critical' | 'High' | 'Medium' | 'Low'})}
        >
          <MenuItem value="Critical">Critical</MenuItem>
          <MenuItem value="High">High</MenuItem>
          <MenuItem value="Medium">Medium</MenuItem>
          <MenuItem value="Low">Low</MenuItem>
        </TextField>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSubmit}
          disabled={loading || !formData.evidence.trim()}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
